
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SectionTitle } from "@/components/SectionTitle";
import { CTASection } from "@/components/CTASection";
import { Button } from "@/components/ui/button";
import { TreatmentServiceCard } from "@/components/TreatmentServiceCard";
import { FAQAccordion } from "@/components/FAQAccordion";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { ServiceCard } from "@/components/ServiceCard";
import { PageTitle } from "@/components/PageTitle";

const GeneralDentistry = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // FAQs specific to General Dentistry
  const faqs = [
    {
      question: "How often should I have a dental check-up?",
      answer: "We recommend visiting every six months for a routine check-up and professional cleaning. However, some patients may need more frequent visits depending on their specific oral health needs."
    },
    {
      question: "What happens during a routine dental check-up?",
      answer: "A routine check-up includes a thorough examination of your teeth, gums, and mouth, professional cleaning to remove plaque and tartar, and may include X-rays to detect issues not visible during the examination."
    },
    {
      question: "How can I maintain good oral hygiene at home?",
      answer: "Brush your teeth twice a day with fluoride toothpaste, floss daily, use mouthwash, maintain a balanced diet, and avoid tobacco products. Regular dental check-ups are also essential for maintaining good oral health."
    },
    {
      question: "What causes tooth decay?",
      answer: "Tooth decay is caused by acids produced by bacteria in plaque. These acids attack the tooth enamel, causing it to break down and form cavities. Regular brushing, flossing, and dental check-ups help prevent tooth decay."
    },
    {
      question: "When should I replace my toothbrush?",
      answer: "You should replace your toothbrush or toothbrush head (for electric toothbrushes) every 3-4 months, or sooner if the bristles are frayed or worn out."
    }
  ];

  // Testimonials for General Dentistry
  const testimonials = [
    {
      name: "Priya Sharma",
      comment: "I've been coming to Asrani Dental for my regular check-ups for the past 3 years. Dr. Hemant and his team are always thorough, professional, and make me feel comfortable. Their preventive care approach has significantly improved my oral health.",
      rating: 5
    },
    {
      name: "Rajesh Kumar",
      comment: "Had a dental filling done at Asrani Dental and was impressed with their gentle approach. Dr. Karan took the time to explain the procedure and made sure I was comfortable throughout. The filling looks natural and feels great.",
      rating: 5
    },
    {
      name: "Anita Desai",
      comment: "The dental cleaning at Asrani Dental is always thorough but gentle. The hygienist is skilled and takes care not to cause any discomfort. I appreciate their focus on education and prevention.",
      rating: 4
    }
  ];

  return (
    <div className="min-h-screen">
      <PageTitle title="Teeth Cleaning in Nagpur | Cost of Teeth Cleaning in Nagpur" />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-dental-offwhite">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-dental-highlight font-medium mb-4 uppercase tracking-wider text-sm">
              Our Services
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold mb-6">
              General Dentistry
            </h1>
            <p className="text-lg text-dental-muted">
              Comprehensive preventive and restorative care to maintain your oral health and keep your smile healthy for years to come.
            </p>
            <div className="mt-8">
              <Button className="btn-primary" onClick={() => window.open("https://www.healthcoco.com/nagpur/doctor/dr-hemant-asrani-endodontist-dentist/USRH249D644", "_blank")}>
                Book Appointment
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Services Overview */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="/lovable-uploads/dc2e9ae6-7e35-473f-926f-c8c1b701bf90.png" 
                alt="General Dentistry at Asrani Dental Clinic" 
                className="rounded-xl shadow-soft w-full h-auto"
              />
            </div>
            
            <div>
              <SectionTitle 
                title="Comprehensive Dental Care" 
                subtitle="General Dentistry"
                description="Our general dentistry services focus on preventing, diagnosing, and treating common dental issues to maintain your oral health and prevent more serious problems from developing."
                alignment="left"
              />
              
              <div className="mt-8 space-y-4">
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Preventive Care</h3>
                    <p className="text-dental-muted">Regular check-ups, cleanings, and early detection of potential problems to maintain optimal oral health.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Restorative Treatments</h3>
                    <p className="text-dental-muted">Fillings, crowns, bridges, and other procedures to repair damaged teeth and restore functionality.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Oral Health Education</h3>
                    <p className="text-dental-muted">Guidance on proper brushing and flossing techniques, nutrition, and lifestyle factors that affect your oral health.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Early Detection</h3>
                    <p className="text-dental-muted">Comprehensive examinations and screenings for oral cancer, gum disease, and other conditions.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Treatments Section */}
      <section className="py-16 md:py-24 bg-dental-cream">
        <div className="container-custom">
          <SectionTitle 
            title="Our General Dentistry Treatments" 
            subtitle="Services We Offer"
            description="We provide a wide range of general dentistry services to address all your oral health needs, from routine care to more complex treatments."
            alignment="center"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            <ServiceCard 
              title="Dental Check-ups & Cleanings"
              description="Regular examinations and professional cleanings to maintain oral health and prevent dental problems."
              imageUrl="/lovable-uploads/79bf0209-ea1e-4839-a4a8-34dc4533acb6.png"
              iconClass="w-10 h-10"
            />
            
            <ServiceCard 
              title="Dental Fillings"
              description="Tooth-colored composite fillings to restore damaged or decayed teeth with natural-looking results."
              imageUrl="/lovable-uploads/41dbeea4-047f-44f4-8002-7151462d749f.png"
              iconClass="w-10 h-10"
            />
            
            <ServiceCard 
              title="Dental Crowns & Bridges"
              description="Custom-made restorations to replace missing teeth or protect damaged teeth."
              imageUrl="/lovable-uploads/833b4b48-2be3-4694-94b8-bc2299fb8d86.png"
              iconClass="w-10 h-10"
            />
            
            <ServiceCard 
              title="Oral Cancer Screenings"
              description="Regular screenings to detect early signs of oral cancer for more effective treatment."
              imageUrl="/lovable-uploads/76f39947-8422-4225-ab51-af0aabfedb9b.png"
              iconClass="w-10 h-10"
            />
            
            <ServiceCard 
              title="Gum Disease Treatment"
              description="Treatments for gum disease, including deep cleaning (scaling and root planing) to restore gum health."
              imageUrl="/lovable-uploads/285110c2-97b3-4b07-a330-a8bbc29b8e1c.png"
              iconClass="w-10 h-10"
            />
            
            <ServiceCard 
              title="Emergency Dental Care"
              description="Prompt care for dental emergencies, including toothaches, broken teeth, and other urgent dental problems."
              imageUrl="/lovable-uploads/a3c0d8d0-fad1-41cf-88e4-d25b7c5d821f.png"
              iconClass="w-10 h-10"
            />
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <SectionTitle 
            title="Patient Testimonials" 
            subtitle="What Our Patients Say"
            description="Hear from our patients about their experiences with our general dentistry services."
            alignment="center"
          />
          
          <div className="max-w-3xl mx-auto mt-12">
            <ServiceTestimonials testimonials={testimonials} />
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-dental-offwhite">
        <div className="container-custom">
          <SectionTitle 
            title="Frequently Asked Questions" 
            subtitle="Common Questions"
            description="Find answers to common questions about general dentistry services."
            alignment="center"
          />
          
          <div className="max-w-3xl mx-auto mt-12">
            <FAQAccordion faqs={faqs} />
          </div>
        </div>
      </section>
      
      {/* Next Steps Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-6">Ready to Schedule Your Appointment?</h2>
            <p className="text-lg text-dental-muted mb-8">
              Contact our office today to schedule your dental check-up or consultation. Our friendly team is here to help you achieve optimal oral health.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button className="btn-primary flex items-center text-base group" onClick={() => window.open("https://www.healthcoco.com/nagpur/doctor/dr-hemant-asrani-endodontist-dentist/USRH249D644", "_blank")}>
                Book Appointment
                <ArrowRight size={18} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
              
              <Button className="btn-secondary flex items-center text-base" onClick={() => window.location.href = '/contact'}>
                Contact Us
              </Button>
            </div>
            
            <div className="mt-10">
              <p className="text-dental-muted mb-4">Interested in our other dental services?</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/services/cosmetic-dentistry" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Cosmetic Dentistry
                </Link>
                <Link to="/services/orthodontics-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Orthodontics
                </Link>
                <Link to="/services/full-mouth-implants-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Full Mouth & Implants
                </Link>
                <Link to="/services/painless-rct-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Painless RCT
                </Link>
                <Link to="/services/pediatric-dentistry-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Pediatric Dentistry
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <CTASection
        title="Keep Your Smile Healthy"
        description="Regular dental check-ups are essential for maintaining good oral health. Schedule your appointment today."
        primaryButtonText="Book an Appointment"
        secondaryButtonText="Learn More"
        primaryButtonAction={() => window.open("https://www.healthcoco.com/nagpur/doctor/dr-hemant-asrani-endodontist-dentist/USRH249D644", "_blank")}
        secondaryButtonAction={() => window.location.href = "/about"}
      />
      
      <Footer />
    </div>
  );
};

export default GeneralDentistry;
